/**
 * SMS Activity thread slideout — the staff console's view into one SMS
 * conversation: the message thread + the card-OCR result.
 *
 * The console runs cross-org WITHOUT impersonation, so the tenant app's live
 * Firestore reads and clinic-scoped callables can't authorize here. Data comes
 * from the staff-gated getSmsThreadForStaff callable (refresh on demand, no
 * subscription); card images from the audited staff getCardImage. Read-only —
 * messaging/mark-handled need a tenant session (impersonation).
 *
 * The panes + `rl-*`/`slideout-*` styling are shared with the tenant
 * RecoveryLoopSlideout (SmsThreadView.tsx).
 */
import { useCallback, useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import {
  getSmsThreadForStaff,
  getCardImageForStaff,
  type SmsActivityRow,
  type SmsThreadResult,
} from '../services/staff'
import { formatPhone, formatIsoDateShort } from '../utils/formatters'
import { smsStatusMeta } from '../utils/smsActivityStatus'
import { ExtractionPane, ConversationThread } from './SmsThreadView'
import { useCardImageUrls, latestCardUrl, caseCardUrl, caseCardImages } from '../utils/smsThread'

interface SmsActivitySlideoutProps {
  row: SmsActivityRow
  onClose: () => void
}

export default function SmsActivitySlideout({ row, onClose }: SmsActivitySlideoutProps) {
  const [tab, setTab] = useState<'conversation' | 'extraction'>('conversation')
  const [thread, setThread] = useState<SmsThreadResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // The effect only kicks off the async fetch — `loading` starts true and the
  // sync spinner flags live in the Refresh handler (react-hooks/set-state-in-effect).
  // The parent keys this component per row, so the effect runs once per open.
  // Threads are keyed by PHONE; a case row's id is its check id, so the phone
  // rides on the row (patientPhone) — a legacy check row without one has no
  // thread to fetch anyway.
  const phone = row.patientPhone ?? row.id
  function fetchThread(orgId: string, p: string) {
    getSmsThreadForStaff(orgId, p)
      .then((t) => { setThread(t); setError(null); setLoading(false) })
      .catch((err) => {
        Sentry.captureException(err)
        setError(err instanceof Error ? err.message : 'Failed to load the thread.')
        setLoading(false)
      })
  }

  useEffect(() => { fetchThread(row.orgId, phone) }, [row.orgId, phone])

  function handleRefresh() {
    setLoading(true)
    setError(null)
    fetchThread(row.orgId, phone)
  }

  const fetchCardImage = useCallback(
    (media: { intakeId: string; objectIndex: number }) =>
      getCardImageForStaff(media.intakeId, media.objectIndex),
    [],
  )
  const messages = thread?.messages ?? []
  const cardUrls = useCardImageUrls(messages, fetchCardImage)
  const inboundCount = messages.filter((m) => m.direction === 'inbound').length

  const conv = thread?.conversation ?? null
  // This slideout opened FROM a case row — render that case's lifecycle; the
  // thread (and any sibling cases) is shared context below it.
  const cs = conv?.cases.find((c) => c.checkId === (row.linkedCheckId ?? row.id)) ?? null
  const siblings = (conv?.cases ?? []).filter((c) => c.checkId !== cs?.checkId)
  const state = cs?.followupState ?? row.status
  const meta = smsStatusMeta(state)
  const name = cs && (cs.patientFirstName || cs.patientLastName)
    ? `${cs.patientFirstName ?? ''} ${cs.patientLastName ?? ''}`.trim()
    : formatPhone(row.patientPhone ?? row.id)
  const extraction = cs?.extraction ?? null
  const illegible = extraction?.status === 'cannot_extract' || state === 'cannot_extract'

  return (
    <>
      <div className="slideout-overlay" onClick={onClose} />
      <aside className="slideout-panel rl-slideout">
        <div className="slideout-panel__header">
          <div className="slideout-panel__header-left rl-slideout__headline">
            <h2 className="slideout-panel__title">{name}</h2>
            <div className="rl-slideout__subrow">
              <span className="rl-slideout__sub">
                {row.practiceName} · {row.clinicName}
                {` · ${formatPhone(row.patientPhone ?? row.id)}`}
                {cs?.appointmentDate ? ` · appt ${formatIsoDateShort(cs.appointmentDate)}` : ''}
              </span>
              <span className={`dsh-status dsh-status--${meta.tone}`} title={state}>
                <span className="dsh-d" />{meta.label}
              </span>
            </div>
          </div>
          <button className="slideout-panel__close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M4.5 4.5l9 9M13.5 4.5l-9 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="rl-slideout__tabs" role="tablist">
          <button
            role="tab"
            aria-selected={tab === 'conversation'}
            className={`rl-slideout__tab${tab === 'conversation' ? ' rl-slideout__tab--active' : ''}`}
            onClick={() => setTab('conversation')}
          >
            Conversation{inboundCount > 0 ? <span className="rl-slideout__tab-badge">{inboundCount}</span> : null}
          </button>
          <button
            role="tab"
            aria-selected={tab === 'extraction'}
            className={`rl-slideout__tab${tab === 'extraction' ? ' rl-slideout__tab--active' : ''}`}
            onClick={() => setTab('extraction')}
          >
            Extraction
          </button>
        </div>

        <div className="rl-slideout__body">
          {error && (
            <div className="rl-callout rl-callout--error">
              <strong>Could not load the thread</strong>
              <p>{error}</p>
            </div>
          )}
          {loading && !thread && <div className="rl-empty-note">Loading thread…</div>}
          {thread && (tab === 'conversation' ? (
            <ConversationThread
              messages={messages}
              cardUrls={cardUrls}
              footnote={illegible ? "Card couldn't be read clearly · routed to staff review" : null}
              extraction={extraction}
              recordPayerName={cs?.payerName ?? null}
              statedFields={cs?.statedFields ?? null}
              caseId={cs?.checkId ?? null}
            />
          ) : (
            <>
              <ExtractionPane
                cardUrl={cs
                  // Case row → that case's own photo (#279): a newer photo
                  // elsewhere on a shared/repeat phone must not stand in for it.
                  ? caseCardUrl(messages, cardUrls, cs.checkId)
                  : latestCardUrl(messages, cardUrls)}
                cardImages={cs ? caseCardImages(messages, cardUrls, cs.checkId) : undefined}
                noCardNote="No card image received yet."
                extraction={extraction}
                illegible={illegible}
                emptyNote="No card extraction yet."
                recordPayerName={cs?.payerName ?? null}
                statedFields={cs?.statedFields ?? null}
              />
              {(cs?.correctionFields?.length || cs?.clarificationFields?.length || siblings.length > 0) ? (
                <>
                  <div className="rl-section-label">Flow context</div>
                  <div className="rl-details">
                    {cs?.correctionFields?.length ? (
                      <div className="rl-details__row">
                        <span className="rl-details__label">Fields needed</span>
                        <span className="rl-details__value">{cs.correctionFields.join(', ')}</span>
                      </div>
                    ) : null}
                    {cs?.clarificationFields?.length ? (
                      <div className="rl-details__row">
                        <span className="rl-details__label">Clarification asked</span>
                        <span className="rl-details__value">{cs.clarificationFields.join(', ')}</span>
                      </div>
                    ) : null}
                    {siblings.map((s) => (
                      <div className="rl-details__row" key={s.checkId}>
                        <span className="rl-details__label">Also on this phone</span>
                        <span className="rl-details__value">
                          {(s.patientFirstName || s.patientLastName)
                            ? `${s.patientFirstName ?? ''} ${s.patientLastName ?? ''}`.trim()
                            : s.checkId}
                          {' · '}
                          {smsStatusMeta(s.followupState ?? 'sms_sent').label}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
            </>
          ))}
        </div>

        <div className="rl-slideout__footer">
          <div className="rl-slideout__actions">
            <button
              className="rl-btn rl-btn--secondary"
              onClick={handleRefresh}
              disabled={loading}
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
            <button className="rl-btn rl-btn--primary" onClick={onClose}>
              Close
            </button>
          </div>
          <div className="rl-slideout__staff-note">
            Cross-org staff view · read-only · card access audited
          </div>
        </div>
      </aside>
    </>
  )
}
